import i18nInst, { type TFunction } from "i18next";

import {
  BOOLEAN_NO_CELL_SIGN,
  BOOLEAN_YES_CELL_SIGN,
  EMPTY_VALUE_DISPLAY,
  LANG_DEFAULT,
  PERIOD_GROUPING_FIELD_NAME,
} from "../../constants";
import type { ITableDto } from "../../dto";
import type { TimeZoneEnum } from "../../enums";
import type { IReportGridColumn } from "../../models";
import { formatDate } from "../date.helpers";
import { twoPointsAfterDecimal } from "../formula.helpers";

const formatReportGridNumber = (value: number): string =>
  new Intl.NumberFormat(i18nInst.language || LANG_DEFAULT, {
    maximumFractionDigits: 2,
  }).format(value);

// CSV export needs a machine-readable number: no grouping and no bidi marks. The grid formatter above is
// locale-aware, and in Hebrew Intl puts a LEFT-TO-RIGHT MARK (U+200E) before the minus sign, so Excel reads
// a negative cell as text and SUM skips it.
const formatReportExportNumber = (value: number): string => String(value);

export const normalizeReportGridValue = ({
  value,
  reportGridColumn,
  timezone,
  t,
  isExport = false,
}: {
  value: unknown;
  reportGridColumn: IReportGridColumn | undefined;
  timezone: TimeZoneEnum;
  t: TFunction;
  isExport?: boolean;
}): unknown => {
  if (value === 0) {
    return 0;
  }

  const {
    isIdColumn,
    isDateColumn,
    dateFormat,
    skipValueProcessing,
    isCheckboxValue,
    translationMap,
  } = reportGridColumn || {};

  if (isIdColumn || skipValueProcessing) {
    return value;
  }

  if (isDateColumn) {
    return formatDate({
      dateISO: value as TDateISO,
      dateFormat,
      timezone,
    });
  }

  if (translationMap) {
    const valueToTranslate =
      translationMap[value as string] || (value as string);
    return t(valueToTranslate);
  }

  if (isCheckboxValue) {
    return value ? BOOLEAN_YES_CELL_SIGN : BOOLEAN_NO_CELL_SIGN;
  }

  const formatNumber = isExport
    ? formatReportExportNumber
    : formatReportGridNumber;

  if (typeof value === "number" && value !== 0 && !skipValueProcessing) {
    const numberValue = twoPointsAfterDecimal(value);
    return formatNumber(numberValue);
  }

  if (value && Number(value) && !skipValueProcessing) {
    return formatNumber(twoPointsAfterDecimal(+value));
  }

  return value;
};

/**
 * Cell value for the client-side CSV export: the normalized value as a string (numbers as plain
 * machine-readable values, see formatReportExportNumber),
 * or EMPTY_VALUE_DISPLAY only for null/undefined/"". Numeric 0 is a real value
 * and must export as "0" (a truthiness check here used to swallow zeros —
 * e.g. Hour at midnight, driver-counted boardings).
 */
export const formatReportExportCellValue = ({
  value,
  reportGridColumn,
  timezone,
  t,
}: {
  value: unknown;
  reportGridColumn: IReportGridColumn | undefined;
  timezone: TimeZoneEnum;
  t: TFunction;
}): string => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_VALUE_DISPLAY;
  }

  // Bare booleans on non-checkbox columns render blank in the grid (React drops raw boolean children),
  // so keep the CSV cell blank too instead of emitting "true"/"false". Checkbox columns fall through to
  // normalizeReportGridValue, which maps them to the YES/NO cell signs shown on screen.
  if (typeof value === "boolean" && !reportGridColumn?.isCheckboxValue) {
    return EMPTY_VALUE_DISPLAY;
  }

  return String(
    normalizeReportGridValue({
      value,
      reportGridColumn,
      timezone,
      t,
      isExport: true,
    }),
  );
};

export const getReportFooterValue = ({
  rows,
  reportGridColumn,
  timezone,
  t,
  isExport = false,
}: {
  rows: ITableDto[];
  reportGridColumn: IReportGridColumn;
  timezone: TimeZoneEnum;
  t: TFunction;
  isExport?: boolean;
}): string | undefined => {
  const {
    uiTableColumn,
    totalType,
    totalWeightColumn,
    totalNumeratorColumn,
    totalDenominatorColumn,
    isPercentage,
  } = reportGridColumn;

  if (
    totalType === null ||
    totalType === undefined ||
    totalType === "NO_TOTAL" ||
    totalType === "TEXT"
  ) {
    return "";
  }

  if (totalType === "ROWS") {
    return String(rows?.length || 0);
  }

  if (totalType === "SUM") {
    const columnSum = rows?.reduce(
      (sum, item) =>
        sum + twoPointsAfterDecimal(Number(item[uiTableColumn] || 0)),
      0,
    );
    const tmpResult = normalizeReportGridValue({
      value: columnSum,
      reportGridColumn,
      timezone,
      t,
      isExport,
    });
    return tmpResult === undefined ? tmpResult : String(tmpResult);
  }

  if (totalType === "MAX") {
    const columnMax = rows?.reduce(
      (max, item) => Math.max(max, Number(item[uiTableColumn] || 0)),
      0,
    );
    const tmpResult = normalizeReportGridValue({
      value: columnMax,
      reportGridColumn,
      timezone,
      t,
      isExport,
    });
    return tmpResult === undefined ? tmpResult : String(tmpResult);
  }

  if (totalType === "MIN") {
    const columnMin = rows?.reduce((min, item) => {
      const value = Number(item[uiTableColumn] || 0);

      if (value === 0) {
        return min;
      }

      return Math.min(min, value);
    }, Number.POSITIVE_INFINITY);
    let tmpResult = normalizeReportGridValue({
      value: columnMin,
      reportGridColumn,
      timezone,
      t,
      isExport,
    });
    tmpResult = tmpResult === undefined ? tmpResult : String(tmpResult);
    return columnMin === Number.POSITIVE_INFINITY
      ? "0"
      : (tmpResult as undefined); // Handle case where all values are 0
  }

  if (totalType === "AVG") {
    if (!rows?.length) {
      return "";
    }

    const columnSum = rows?.reduce(
      (sum, item) => sum + Number(item[uiTableColumn] || 0),
      0,
    );
    const averageAmount = columnSum / rows.length;

    const tmpResult = normalizeReportGridValue({
      value: averageAmount,
      reportGridColumn,
      timezone,
      t,
      isExport,
    });
    return tmpResult === undefined ? tmpResult : String(tmpResult);
  }

  // Ride-weighted average: SUM(value_i * weight_i) / SUM(weight_i). The weight is another column in the same
  // row (e.g. the count of rides that actually contributed to the average), so the grand total reflects group
  // size instead of being a plain average-of-averages.
  // NOTE: totalWeightColumn (and the PERCENT numerator/denominator columns below) must be present on every row
  // DTO even when they are hidden from the grid — the backend addSummaryColumns force-selects any column a
  // visible column's total references, so the value is always in the row here.
  if (totalType === "WEIGHTED_AVG") {
    if (!rows?.length || !totalWeightColumn) {
      return "";
    }

    let weightedSum = 0;
    let weightTotal = 0;
    rows.forEach(item => {
      const value = Number(item[uiTableColumn] || 0);
      const weight = Number(item[totalWeightColumn] || 0);

      // A 0/blank value means the group had no data for this metric (the backend excludes 0/missing from the
      // per-group average), so drop the row and its weight — otherwise it would pull the weighted mean down.
      if (!value || !weight) {
        return;
      }

      weightedSum += value * weight;
      weightTotal += weight;
    });

    if (weightTotal === 0) {
      return "";
    }

    const weightedAverage = weightedSum / weightTotal;
    const tmpResult = normalizeReportGridValue({
      value: weightedAverage,
      reportGridColumn,
      timezone,
      t,
      isExport,
    });
    return tmpResult === undefined ? tmpResult : String(tmpResult);
  }

  // Ratio of sums: SUM(numerator_i) / SUM(denominator_i), the true overall rate for a percentage / per-unit
  // metric (e.g. boarding rate, occupancy) instead of an average of per-group percentages. Scaled by 100 for
  // percentage columns. We return the bare number here — for isPercentage columns the "%" suffix is added by
  // the footer renderer (resolveReportColumnFooterPrefix -> CellFooterSummary), so it must not be appended here.
  if (totalType === "PERCENT") {
    if (!rows?.length || !totalNumeratorColumn || !totalDenominatorColumn) {
      return "";
    }

    let numerator = 0;
    let denominator = 0;
    rows.forEach(item => {
      numerator += Number(item[totalNumeratorColumn] || 0);
      denominator += Number(item[totalDenominatorColumn] || 0);
    });

    if (denominator === 0) {
      return "";
    }

    const ratio = (numerator / denominator) * (isPercentage ? 100 : 1);
    const tmpResult = normalizeReportGridValue({
      value: ratio,
      reportGridColumn,
      timezone,
      t,
      isExport,
    });
    return tmpResult === undefined ? tmpResult : String(tmpResult);
  }

  return "";
};

export const reportIncludesAllFlexGroupingColumns = ({
  visibleColumnsReport,
  groupingColumns,
  isFlexPeriodTemplateApplied,
}: {
  visibleColumnsReport: string[] | undefined;
  groupingColumns: string[] | undefined;
  isFlexPeriodTemplateApplied: boolean | undefined;
}): boolean => {
  if (!visibleColumnsReport) {
    return false;
  }

  if (!groupingColumns && !isFlexPeriodTemplateApplied) {
    return true;
  }

  if (groupingColumns) {
    const visibleColumnsReportSet = new Set(visibleColumnsReport);
    if (!groupingColumns.every(column => visibleColumnsReportSet.has(column))) {
      return false;
    }
  }

  if (
    isFlexPeriodTemplateApplied &&
    !visibleColumnsReport.includes(PERIOD_GROUPING_FIELD_NAME)
  ) {
    return false;
  }

  return true;
};
